import { PropsWithChildren, useEffect, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { RxDocument } from 'rxdb'

import { canPlayCard } from '~/boundedContext/cards/utils/card'
import type { Card } from '~/db/types'
import { useCardsGameStore } from '~/stores/cards-game'
import type { TRoutes } from '~/types/routes'

export type CardsRedirectProps = PropsWithChildren & {
  activeCard?: RxDocument<Card>
  paths?: {
    finish: TRoutes
    noCards: TRoutes
    noCardsInStack: TRoutes
  }
}

/**
 * Redirects are defined here, as we can't solely rely on navigation on action
 * as user can manipulate the cards list outside of the game screen.
 */
export const CardsRedirect = ({
  children,
  paths = {
    finish: '/cards/finish',
    noCards: '/cards/no-cards',
    noCardsInStack: '/cards/no-cards-in-stack',
  },
  activeCard,
}: CardsRedirectProps) => {
  const notGuessedCards = useCardsGameStore(state => state.notGuessedCards)
  const allCards = useCardsGameStore(state => state.allCards)
  const isInitialized = useCardsGameStore(state => state.isInitialized)

  const navigate = useNavigate()

  const noActiveCard = !activeCard
  const cardsExceptSnoozed = useMemo(() => allCards.filter(card => canPlayCard(card)), [allCards])

  const noCardsToPlay = !cardsExceptSnoozed.length

  useEffect(() => {
    if (!isInitialized) return

    if (noCardsToPlay) {
      void navigate({ to: paths.noCards })

      return
    }

    if (noActiveCard && !notGuessedCards.length) {
      void navigate({ to: paths.finish })

      return
    }

    if (noActiveCard && notGuessedCards.length) {
      void navigate({ to: paths.noCardsInStack })

      return
    }
  }, [
    noActiveCard,
    noCardsToPlay,
    isInitialized,
    notGuessedCards.length,
    navigate,
    paths.finish,
    paths.noCards,
    paths.noCardsInStack,
  ])

  if (!isInitialized || !activeCard) return null

  return children
}
